import React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { azure, glass, line, lineStrong, slate } from '../theme';

// Served from public/ rather than imported: an import under 10KB gets inlined
// into the main bundle as base64, so every visitor would download a photo
// that sits in a closed panel. As a file, the lazy <img> fetches it on demand.
const founderPhoto = `${process.env.PUBLIC_URL || ''}/assets/img/andrzej-goralski.jpg`;

// Names stay in Latin script in both languages, as product names do.
const PEOPLE = [
  { name: 'Andrzej Stanislaw Goralski', role: 'founder', photo: founderPhoto },
  { name: 'Kassim Hassan', role: 'regional' },
  { name: 'Martin Labbe', role: 'it' },
  { name: 'Marzena Goralska', role: 'service' },
];

export const LEADERSHIP_ID = 'leadership';

/**
 * The team, folded away in the footer on purpose: it is there for a visitor
 * who goes looking, not something the pages lead with. It opens from the
 * "Leadership" link in the footer's Company column, which owns `open`.
 *
 * The panel stays mounted while closed (Collapse hides it with visibility,
 * not unmounting), so the names are still in the prerendered HTML. The photo
 * is lazy, so nobody downloads it until the footer is near.
 */
export default function Leadership({ open }) {
  const { t } = useTranslation();
  const panelRef = React.useRef(null);

  // On a phone the Company column sits above the Contact column, so the panel
  // opens out of sight below the link that opened it. Bring it into view.
  const reveal = () => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

  return (
    <Collapse in={open} onEntered={reveal}>
      <Box sx={{ pt: { xs: 5, md: 6 } }}>
        <Box
          ref={panelRef}
          id={LEADERSHIP_ID}
          component="section"
          aria-labelledby={`${LEADERSHIP_ID}-title`}
          sx={{
            p: { xs: 3, md: 3.5 },
            borderRadius: 2.5,
            border: `1px solid ${line}`,
            bgcolor: glass,
            // Room below the panel when it is scrolled into view, so it does
            // not land flush against the bottom edge of the screen.
            scrollMarginBottom: 24,
          }}
        >
          <Typography
            id={`${LEADERSHIP_ID}-title`}
            variant="overline"
            component="p"
            sx={{ color: azure[300] }}
          >
            {t('leadership.title')}
          </Typography>

          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: { xs: 2.5, md: 3 },
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.35fr 1fr 1fr 1fr' },
              alignItems: 'center',
            }}
          >
            {PEOPLE.map((p) => (
              <Box key={p.name} sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                {p.photo && (
                  // Shown as taken: no tint or filter, so it reads as a photograph
                  // rather than an illustration. The name beside it is the label.
                  <Box
                    component="img"
                    src={p.photo}
                    alt=""
                    width={56}
                    height={56}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: 56,
                      height: 56,
                      flex: 'none',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      boxShadow: `0 0 0 1px ${lineStrong}, 0 6px 18px rgba(0, 0, 0, 0.35)`,
                    }}
                  />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: slate[100], lineHeight: 1.35 }}>
                    {p.name}
                  </Typography>
                  <Typography variant="caption" component="p" sx={{ color: 'text.secondary' }}>
                    {t(`leadership.${p.role}`)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Collapse>
  );
}
